import React, { Fragment, useEffect, useState } from "react";
import { FRPProject, FRPUserAccessRights } from "./FRPState";
import { ADModalBody, ADModalFooter, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { GlassInputControl, GlassInputGroup, GlassInputGroupEscape, GlassInputGroupTitle, GlassTextInputTransformer } from "src/components/GlassInput";
import { GlassButton } from "src/components/GlassButtons";
import { ApplicationStateContext, Feature } from "src/states/ApplicationState";
import { GlassCheckbox } from "src/components/MitGlassComponents";
import { MIBIcon } from "src/componentsUtils/MIBIcon";

interface _FormData {
  userEmail: string,
  customerDomain: string,
  product: string;
  productVariant: string;
  expires: string;
  licenseType: string;
  note: string;
  customer: string;
  roles: SubFormEntryString[];
  accessRightsUsers: SubFormEntry[];
  accessRightsOrganizations: SubFormEntry[];
}

interface SubFormEntry { name: string, value: string };
type SubFormEntryString = string;

function SubForm(props: { subFormData: SubFormEntry[], setSubFormData: (value: SubFormEntry[]) => void }) {

  useEffect(() => {
    console.log("Subform data", props.subFormData)
  }, [props.subFormData]);

  const addSubFormEntry = () => {
    props.setSubFormData([...props.subFormData, { name: '', value: '' }]);
  };

  const removeSubFormEntry = (index: number) => {
    const updatedSubforms = [...props.subFormData];
    updatedSubforms.splice(index, 1);
    props.setSubFormData(updatedSubforms);
  };

  const handleSubFormChange = (
    index: number,
    field: string,
    event: React.ChangeEvent<any>
  ) => {
    const { value } = event.target;
    const updatedSubforms = props.subFormData.map((item, idx) => {
      return idx === index ? field === "name" ?
        { name: value, value: item.value } : { name: item.name, value: value } : item;
    });
    props.setSubFormData(updatedSubforms);
  };

  return (
    <>
      <GlassInputGroupEscape >
        {props.subFormData.map((subform, index) => (
          <div key={index} className="GlassInputField" style={{ marginBottom: "5px" }} >
            <GlassInputControl>
              <input type="text" value={subform.name} onChange={(e) => handleSubFormChange(index, "name", e)} placeholder="Email" autoComplete="off" />


              <select
                style={{ flex: "auto 1 0" }}
                name="product"
                value={subform.value || ""}
                onChange={(e) => handleSubFormChange(index, "value", e)}
              >
                {subform.value == "" && <option value="">Select a access</option>}
                {[FRPUserAccessRights.Read, FRPUserAccessRights.Write, FRPUserAccessRights.Admin].map((product: string) => {
                  return (
                    <option key={product} value={product}>{product}</option>
                  )
                })}
              </select>


            </GlassInputControl>
            <div className="Setting_Butto" title="Slet bruger" >
              <span onClick={() => removeSubFormEntry(index)}>
                <MIBIcon icon="delete" />
              </span>
            </div>
          </div>
        ))}
        <GlassButton style={{ marginLeft: "auto" }} onClick={addSubFormEntry}>
          Add Entry
        </GlassButton >
      </GlassInputGroupEscape>
    </>
  );
};

// Validates if a string is valid for use in an Elasticsearch index name
// Index names must be lowercase and cannot contain: \, /, *, ?, ", <, >, |, space, comma
// Cannot start with _, -, +, . or ..
function isValidElasticsearchIndexNamePart(name: string): { valid: boolean; error?: string } {
  if (!name || name.trim().length === 0) {
    return { valid: false, error: "Project ID cannot be empty" };
  }

  if (name !== name.toLowerCase()) {
    return { valid: false, error: "Project ID must be lowercase" };
  }

  const invalidChars = /[\\/*?"<>|\s,]/;
  if (invalidChars.test(name)) {
    return { valid: false, error: "Project ID cannot contain: \\, /, *, ?, \", <, >, |, space, or comma" };
  }

  if (name.startsWith('_') || name.startsWith('-') || name.startsWith('+') || name.startsWith('.')) {
    return { valid: false, error: "Project ID cannot start with _, -, +, or ." };
  }

  if (name === '.' || name === '..') {
    return { valid: false, error: "Project ID cannot be '.' or '..'" };
  }

  if (name.length > 255) {
    return { valid: false, error: "Project ID cannot exceed 255 characters" };
  }

  return { valid: true };
}

function InputFormWithSubForm(props: { value: FRPProject, onChange: (project: FRPProject) => void, onClose: () => void }) {
  const { hasAccessToFeature } = React.useContext(ApplicationStateContext);
  const [data, setData] = useState<FRPProject>(props.value);
  const [validationError, setValidationError] = useState<string | undefined>(undefined);

  function onchange() {
    const validation = isValidElasticsearchIndexNamePart(data.id);
    if (!validation.valid) {
      setValidationError(validation.error);
      return;
    }
    setValidationError(undefined);
    props.onChange(data);
  }

  const handleInputChange = (
    key: keyof FRPProject, value
  ) => {
    setData((prevData) => ({ ...prevData, [key]: value }));
    // Clear validation error when user starts typing
    if (key === "id" && validationError) {
      const validation = isValidElasticsearchIndexNamePart(value);
      if (validation.valid) {
        setValidationError(undefined);
      } else {
        setValidationError(validation.error);
      }
    }
  };

  function transformData(data: Record<string, string[]>): SubFormEntry[] {
    return data ? Object.keys(data).map((key) => {
      return data[key]?.map?.((a) => {
        return { name: key, value: a }
      }) || [{ name: key, value: "" }]
    }).flat() : []
  }

  return (
    <>
      <ADModalBody>
        <GlassInputGroup onSubmit={() => onchange()} onEscape={props.onClose}>
          <GlassTextInputTransformer label={"Project ID"}>
            <GlassInputControl>
              <input type="text" value={data.id} onChange={(e) => handleInputChange("id", e.target.value)} />
              {validationError && (
                <div style={{ color: "#e74c3c", fontSize: "0.85em", marginTop: "4px" }}>
                  {validationError}
                </div>
              )}
            </GlassInputControl>
          </GlassTextInputTransformer>
          <GlassTextInputTransformer label={"Project name"}>
            <input type="text" value={data.name} onChange={(e) => handleInputChange("name", e.target.value)} />
          </GlassTextInputTransformer>
          <GlassTextInputTransformer label={"Project Description"}>
            <GlassInputControl>
              <textarea value={data.description} onChange={(e) => handleInputChange("description", e.target.value)} />
            </GlassInputControl>
          </GlassTextInputTransformer>
          <GlassTextInputTransformer label={"Protected"}>
            <GlassCheckbox disabled={!hasAccessToFeature(Feature.FRPToolGlobalAdmin)} checked={data.protected} onClick={(e, val) => handleInputChange("protected", val)} ></GlassCheckbox>
            {!hasAccessToFeature(Feature.FRPToolGlobalAdmin) ? (<div style={{ marginLeft: "5pt", fontSize: "0.8em", color: "grey" }}>
              (Must be Global Admin to change)
            </div>) : null}
          </GlassTextInputTransformer>
          <GlassInputGroupTitle>Access Rights</GlassInputGroupTitle>
          <SubForm
            subFormData={transformData(data.allowedUsersWithRoles)}
            setSubFormData={(value: SubFormEntry[]) => {
              let resData = {}
              value.forEach((entry) => {
                if (resData[entry.name]) {
                  resData[entry.name].push(entry.value)
                }
                else {
                  resData[entry.name] = [entry.value]
                }
              })
              setData((prevData) => ({ ...prevData, allowedUsersWithRoles: resData }));
            }}
          />
        </GlassInputGroup>
      </ADModalBody>
      <ADModalFooter>
        <GlassButton onClick={() => onchange()} disabled={!!validationError || !data.id}>
          Submit
        </GlassButton>
        <GlassButton onClick={props.onClose}>
          Close
        </GlassButton>
      </ADModalFooter>
    </>
  );
};

export default InputFormWithSubForm;

export const FRPProjectEditor = (props: {
  title?: string,
  show: boolean,
  value: FRPProject,
  onClose: () => void,
  onChange: (project: FRPProject) => void
}) => {

  if (!props.show) {
    return <></>
  }

  return (
    <Fragment key={props.value.id}>
      <AdvancedDragModal title={props.title ?? "Project Editor"} PosDefault={{ width: "450px", left: "50%", transform: "translateX(-50%)", top: "100px" }} onClose={() => props.onClose()}>
        <InputFormWithSubForm value={props.value} onChange={(e) => props.onChange(e)} onClose={() => props.onClose()} />
      </AdvancedDragModal>
    </Fragment>
  )
}